/**
 * Where many new nodes start (a project opened, a big scan merged): close to where the layout
 * will put them, so it settles in a few steps instead of untangling a heap.
 *
 * Dropped all at once in one small square, 40 000 nodes would all be within reach of each
 * other: the push between nodes (`repulsion.ts`) would have to meet nearly every pair, and the
 * layout would take minutes to spread them. Instead each group of new nodes (the nodes of one
 * group, or in no group the nodes of one first category) gets a disc as big as its nodes need,
 * and the discs are laid on a sunflower spiral (each one a golden angle further round, as far
 * out as the area of the discs before it), the biggest in the middle. Inside a disc the nodes
 * are on a small sunflower of their own. The forces then only tidy up.
 */
import type { SimNode } from "./types";

/** From this many new nodes at once, they are seeded; fewer just appear near their corner. */
export const SEED_FROM = 60;

/** The room one node takes in a disc, in world units (at spacing 1): about a link's length. */
const NODE_ROOM = 46;

/** The turn between two neighbours of a sunflower spiral, in radians. */
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

/** What a new node is seeded with: its group, else its first category, else nothing. */
function family(n: SimNode): string {
  return n.node.group ? `g:${n.node.group}` : `c:${(n.node.categories[0] ?? "").toLowerCase()}`;
}

/**
 * Places `nodes` (new ones, on the plane) in discs by family around `centre`, `spacing` apart.
 * The same nodes in the same order always get the same places.
 */
export function seedPositions(nodes: readonly SimNode[], centre: readonly [number, number], spacing: number): void {
  const families = new Map<string, SimNode[]>();
  for (const n of nodes) {
    const key = family(n);
    const list = families.get(key);
    if (list) list.push(n);
    else families.set(key, [n]);
  }
  // The biggest families in the middle, where there is the most room around them.
  const ordered = [...families.values()].sort((a, b) => b.length - a.length);
  const room = NODE_ROOM * spacing;
  let area = 0;
  ordered.forEach((members, k) => {
    const radius = room * Math.sqrt(members.length / Math.PI) + room;
    // The disc goes as far out as the discs before it fill, plus its own half.
    const out = k === 0 ? 0 : Math.sqrt(area / Math.PI) + radius;
    area += Math.PI * (radius * 1.25) ** 2;
    const cx = centre[0] + Math.cos(k * GOLDEN_ANGLE) * out;
    const cy = centre[1] + Math.sin(k * GOLDEN_ANGLE) * out;
    members.forEach((n, i) => {
      const r = room * Math.sqrt((i + 0.5) / Math.PI);
      n.x = cx + Math.cos(i * GOLDEN_ANGLE) * r;
      n.y = cy + Math.sin(i * GOLDEN_ANGLE) * r;
      n.z = 0;
    });
  });
}
